/**
 * One table for the two spellings the app uses for each min/max filter pair.
 *
 * The forms, stores and filter popups all speak the UI spelling ("couponMin"). The backend
 * reads only the canonical spelling ("minCoupon" — BondsController, WatchlistController,
 * SecMasterFilterParamsParser, FilterCriteria); the UI spelling appears nowhere in its
 * source. A saved characteristic/ladder JSON is not just persisted, it is later replayed
 * through the filter API, so it is stored in the canonical spelling and expanded back to
 * the UI spelling only when it is loaded into a form.
 *
 * canonical (what the backend reads) -> alias (UI spelling)
 */
export const FILTER_KEY_ALIASES = {
  minMaturity: "maturityMin",
  maxMaturity: "maturityMax",
  minCoupon: "couponMin",
  maxCoupon: "couponMax",
  minYtm: "yieldMin",
  maxYtm: "yieldMax",
  minPrice: "priceMin",
  maxPrice: "priceMax",
  minQty: "qtyMin",
  maxQty: "qtyMax",
};

const hasValue = (v) => v !== undefined && v !== null && v !== "";

/**
 * Collapse each pair to the single key the backend reads. Use on anything persisted or
 * sent over the wire.
 *
 * Deliberately limited to the min/max pairs: callable/call is wire-only (see api.js) and
 * the rating keys are left alone, because minimumRating is the UI's own source of truth
 * and dropping it would blank the Edit modal's rating dropdown.
 */
export const toBackendFilterKeys = (obj) => {
  if (!obj || typeof obj !== "object") return obj;

  const out = { ...obj };
  Object.entries(FILTER_KEY_ALIASES).forEach(([canonical, alias]) => {
    // Resolve before deleting, so an empty canonical can still fall back to the alias
    const value = [out[canonical], out[alias]].find(hasValue);
    delete out[alias];
    if (hasValue(value)) out[canonical] = value;
    else delete out[canonical];
  });
  return out;
};

/**
 * Re-add the UI spelling alongside the canonical one. Use wherever a stored characteristics
 * JSON is parsed back into something a form or chip reads, since those are keyed by the UI
 * spelling. Non-destructive: an existing alias value always wins.
 */
export const withUiFilterKeyAliases = (obj) => {
  if (!obj || typeof obj !== "object") return obj;

  const out = { ...obj };
  Object.entries(FILTER_KEY_ALIASES).forEach(([canonical, alias]) => {
    if (!hasValue(out[alias]) && hasValue(out[canonical])) out[alias] = out[canonical];
  });
  return out;
};
